/*
## Summary
Application cache composable. Manages global app configuration and data caching with reactive updates.

## Responsibilities
- Initialize and manage app-wide cache store
- Provide reactive access to cached app data
- Handle cache invalidation and refresh
- Manage app configuration state
- Track cache loading and error states

## Out of Scope
- Component-level caching (each component manages own)
- Session data persistence
- Authentication token storage
- Real-time updates (delegated to RealtimeService)
- Disk storage and persistence

## Dependencies
- Vue 3 reactivity
- Application state management
- Services for data fetching
- Type definitions

## Notes
Provides single source of truth for app-wide cached data.
*/
import { reactive } from 'vue'

type CacheEntry<T> = { value: T; expiresAt: number | null }

export interface SetOptions {
  /** Milliseconds until the entry expires. Omit for no expiry. */
  ttlMs?: number
  /** When true the entry is persisted to localStorage under the key mac:<key>. */
  persist?: boolean
}

export interface GetOrFetchOptions extends SetOptions {
  /** When true, bypass cache and refetch. */
  force?: boolean
}

export interface SWRResult<T> {
  /** The cached (possibly stale) value, or the freshly fetched value on cold miss. */
  value: T
  /** True when the value was served from an expired entry (background refetch is running). */
  isStale: boolean
  /**
   * Present when a background revalidation fetch is in progress.
   * Resolves with the fresh value when the fetch completes.
   * Null when the data was fresh or this was a cold miss (value already awaited).
   */
  revalidating: Promise<T> | null
}

const store = reactive<Record<string, CacheEntry<unknown>>>({})

export const __cacheStats = { hits: 0, misses: 0 }

const LS_PREFIX = 'mac:'
const LRU_MAX = 200
const LRU_EVICT_RATIO = 0.1

/** Tracks insertion order for LRU eviction (memory layer only). */
const insertionOrder: string[] = []

/** In-flight fetches keyed by cache key — dedupes concurrent getOrFetch callers. */
const pending = new Map<string, Promise<unknown>>()

// Resolved once at module load time; works regardless of tsconfig include scope.
const IS_DEV = ((): boolean => {
  try {
    return (import.meta as Record<string, unknown>).env?.DEV === true
  } catch {
    return false
  }
})()

function lsGet<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (!raw) return null
    return JSON.parse(raw) as CacheEntry<T>
  } catch {
    return null
  }
}

function lsSet<T>(key: string, entry: CacheEntry<T>): void {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry))
  } catch {
    // quota exceeded or private mode — silently skip
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(LS_PREFIX + key)
  } catch {
    // silently skip
  }
}

function touchInsertionOrder(key: string): void {
  const idx = insertionOrder.indexOf(key)
  if (idx >= 0) insertionOrder.splice(idx, 1)
  insertionOrder.push(key)
}

function removeFromInsertionOrder(key: string): void {
  const idx = insertionOrder.indexOf(key)
  if (idx >= 0) insertionOrder.splice(idx, 1)
}

function evictLruIfNeeded(): void {
  if (insertionOrder.length <= LRU_MAX) return
  const evictCount = Math.max(1, Math.floor(LRU_MAX * LRU_EVICT_RATIO))
  for (let i = 0; i < evictCount && insertionOrder.length > 0; i++) {
    const oldKey = insertionOrder.shift()
    if (oldKey === undefined) break
    if (oldKey in store) delete store[oldKey]
    lsRemove(oldKey)
    if (IS_DEV) console.debug(`[cache] EVICT ${oldKey}`)
  }
}

export function useAppCache() {
  function get<T>(key: string): T | undefined {
    const now = Date.now()

    // 1. Check in-memory store
    if (key in store) {
      const entry = store[key] as CacheEntry<T>
      if (entry.expiresAt !== null && entry.expiresAt < now) {
        delete store[key]
        removeFromInsertionOrder(key)
        lsRemove(key)
        __cacheStats.misses++
        if (IS_DEV) console.debug(`[cache] EXPIRED ${key}`)
        return undefined
      }
      __cacheStats.hits++
      if (IS_DEV) console.debug(`[cache] HIT  ${key}`)
      return entry.value as T
    }

    // 2. Try localStorage rehydration
    const persisted = lsGet<T>(key)
    if (persisted) {
      if (persisted.expiresAt !== null && persisted.expiresAt < now) {
        lsRemove(key)
        __cacheStats.misses++
        if (IS_DEV) console.debug(`[cache] EXPIRED(ls) ${key}`)
        return undefined
      }
      store[key] = persisted as CacheEntry<unknown>
      touchInsertionOrder(key)
      __cacheStats.hits++
      if (IS_DEV) console.debug(`[cache] HIT(ls) ${key}`)
      return persisted.value
    }

    __cacheStats.misses++
    if (IS_DEV) console.debug(`[cache] MISS ${key}`)
    return undefined
  }

  function set<T>(key: string, value: T, opts?: SetOptions): void {
    const expiresAt = opts?.ttlMs ? Date.now() + opts.ttlMs : null
    const entry: CacheEntry<T> = { value, expiresAt }
    store[key] = entry as CacheEntry<unknown>
    touchInsertionOrder(key)
    evictLruIfNeeded()
    if (opts?.persist) lsSet(key, entry)
  }

  async function getOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    opts?: GetOrFetchOptions,
  ): Promise<T> {
    if (!opts?.force) {
      const cached = get<T>(key)
      if (cached !== undefined) return cached
    } else if (IS_DEV) {
      console.debug(`[cache] FORCE ${key}`)
    }

    const inFlight = pending.get(key)
    if (inFlight) return inFlight as Promise<T>

    if (IS_DEV) console.debug(`[cache] FETCH ${key}`)

    // Register in-flight synchronously so concurrent callers dedupe before fetcher runs.
    const promise = (async () => {
      try {
        const value = await fetcher()
        set(key, value, { ttlMs: opts?.ttlMs, persist: opts?.persist })
        return value
      } finally {
        pending.delete(key)
      }
    })()

    pending.set(key, promise)
    return promise
  }

  /**
   * Read a cache entry without deleting it, even if expired.
   * Returns undefined only when there is truly no value (memory + localStorage miss).
   * Used by the SWR path to serve stale data immediately.
   */
  function peek<T>(key: string): { value: T; isStale: boolean } | undefined {
    const now = Date.now()

    if (key in store) {
      const entry = store[key] as CacheEntry<T>
      const isStale = entry.expiresAt !== null && entry.expiresAt < now
      return { value: entry.value as T, isStale }
    }

    const persisted = lsGet<T>(key)
    if (persisted) {
      const isStale = persisted.expiresAt !== null && persisted.expiresAt < now
      if (!isStale) {
        // Rehydrate into memory (same as get)
        store[key] = persisted as CacheEntry<unknown>
        touchInsertionOrder(key)
      }
      return { value: persisted.value, isStale }
    }

    return undefined
  }

  /**
   * Stale-while-revalidate read.
   *
   * - Fresh hit  → return immediately; no background fetch.
   * - Stale hit  → return stale value immediately + kick off background fetch (deduped).
   *                The returned `revalidating` promise resolves when fresh data is stored.
   * - Cold miss  → await fetcher, store result, return fresh value (isStale=false, revalidating=null).
   */
  async function getOrSWR<T>(
    key: string,
    fetcher: () => Promise<T>,
    opts?: GetOrFetchOptions,
  ): Promise<SWRResult<T>> {
    if (opts?.force) {
      if (IS_DEV) console.debug(`[cache] FORCE(swr) ${key}`)
      const value = await (async () => {
        const inFlight = pending.get(key) as Promise<T> | undefined
        if (inFlight) return inFlight
        const promise = (async () => {
          try {
            const v = await fetcher()
            set(key, v, { ttlMs: opts?.ttlMs, persist: opts?.persist })
            if (IS_DEV) console.debug(`[cache] REVALIDATED ${key}`)
            return v
          } finally {
            pending.delete(key)
          }
        })()
        pending.set(key, promise)
        return promise
      })()
      return { value, isStale: false, revalidating: null }
    }

    const peeked = peek<T>(key)

    // Cold miss — block until fresh data arrives
    if (peeked === undefined) {
      if (IS_DEV) console.debug(`[cache] MISS(swr) ${key}`)
      const inFlight = pending.get(key) as Promise<T> | undefined
      if (inFlight) {
        const value = await inFlight
        return { value, isStale: false, revalidating: null }
      }
      const promise = (async () => {
        try {
          const v = await fetcher()
          set(key, v, { ttlMs: opts?.ttlMs, persist: opts?.persist })
          if (IS_DEV) console.debug(`[cache] REVALIDATED ${key}`)
          return v
        } finally {
          pending.delete(key)
        }
      })()
      pending.set(key, promise)
      const value = await promise
      return { value, isStale: false, revalidating: null }
    }

    // Fresh hit — return immediately, no fetch
    if (!peeked.isStale) {
      __cacheStats.hits++
      if (IS_DEV) console.debug(`[cache] HIT(swr) ${key}`)
      return { value: peeked.value, isStale: false, revalidating: null }
    }

    // Stale hit — serve immediately + background refetch
    __cacheStats.hits++
    if (IS_DEV) console.debug(`[cache] STALE-SERVE ${key}`)

    const existingInFlight = pending.get(key) as Promise<T> | undefined
    if (existingInFlight) {
      return { value: peeked.value, isStale: true, revalidating: existingInFlight }
    }

    const revalidating = (async () => {
      try {
        const v = await fetcher()
        set(key, v, { ttlMs: opts?.ttlMs, persist: opts?.persist })
        if (IS_DEV) console.debug(`[cache] REVALIDATED ${key}`)
        return v
      } finally {
        pending.delete(key)
      }
    })()
    pending.set(key, revalidating)

    return { value: peeked.value, isStale: true, revalidating }
  }

  function invalidate(key: string): void {
    if (key in store) delete store[key]
    removeFromInsertionOrder(key)
    lsRemove(key)
    pending.delete(key)
  }

  function invalidatePattern(prefix: string): void {
    for (const key of Object.keys(store)) {
      if (key.startsWith(prefix)) {
        delete store[key]
        removeFromInsertionOrder(key)
        lsRemove(key)
        pending.delete(key)
      }
    }
    // Also sweep localStorage for keys that may have been persisted but not yet rehydrated
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const lsKey = localStorage.key(i)
        if (lsKey?.startsWith(LS_PREFIX + prefix)) {
          localStorage.removeItem(lsKey)
        }
      }
    } catch {
      // silently skip
    }
  }

  function clear(): void {
    for (const key of Object.keys(store)) {
      delete store[key]
    }
    insertionOrder.length = 0
    pending.clear()
    // Clear all mac: prefixed localStorage entries
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const lsKey = localStorage.key(i)
        if (lsKey?.startsWith(LS_PREFIX)) {
          localStorage.removeItem(lsKey)
        }
      }
    } catch {
      // silently skip
    }
  }

  return { get, set, peek, getOrFetch, getOrSWR, invalidate, invalidatePattern, clear }
}
